import React, { useContext } from 'react';
import Chart from 'react-apexcharts';
import ApexCharts from 'apexcharts';
import ptBr from 'apexcharts/dist/locales/pt-br.json';
import en from 'apexcharts/dist/locales/en.json';
import la from 'apexcharts/dist/locales/es.json';
import { LanguageContext } from 'contexts/LanguageContext';
import { WidgetDataByDate } from 'interfaces/widgetData';
import { Skeleton } from 'antd';

interface Props {
  widgetData: WidgetDataByDate | undefined;
}

export function WidgetDataChart({ widgetData }: Props) {
  const { dictionary } = useContext(LanguageContext);

  const getOptions = (): ApexCharts.ApexOptions => {
    if (!widgetData) {
      return {};
    }

    const xaxis = [];

    for (const year of Object.keys(widgetData)) {
      for (const month of Object.keys(widgetData[year])) {
        const label = formatDateForLabel(month, year);
        xaxis.push(label);
      }
    }

    const locale = dictionary.locale;
    const title = dictionary.chart_widget_title;

    const totalMonths = Object.keys(widgetData).reduce((count, year) => {
      return count + Object.keys(widgetData[year]).length;
    }, 0);

    return {
      title: { text: title },
      chart: {
        id: 'widget-data',
        background: '#fff',
        defaultLocale: locale,
        locales: [ptBr, en, la],
        toolbar: {
          offsetY: 30,
        },
      },
      plotOptions: {
        bar: {
          dataLabels: {
            position: 'top',
            orientation: 'vertical',
          },
          columnWidth: '30%',
        },
      },
      xaxis: {
        categories: xaxis,
        range: totalMonths > 12 ? 12 : totalMonths,
      },
      dataLabels: {
        style: {
          fontSize: '10px',
          colors: ['#373D3F'],
        },
      },
      grid: {
        padding: { bottom: -10 },
      },
      yaxis: {
        labels: {
          formatter: (value: number) => {
            let newValue;
            if (value) {
              newValue = value.toLocaleString();
            }
            return newValue ?? String(value);
          },
        },
      },
    };
  };

  const formatDateForLabel = (month: string, year: string) => {
    let formattedMonth = month;
    if (Number(month) < 10) {
      formattedMonth = '0' + month;
    }

    const formattedYear = year.slice(-2);

    return `${formattedMonth}/${formattedYear}`;
  };

  const getSeries = () => {
    if (!widgetData) {
      return [];
    }

    const clickSeries = [];
    const redirectSeries = [];

    for (const year of Object.keys(widgetData)) {
      for (const month of Object.keys(widgetData[year])) {
        let totalClicks = 0;
        let totalRedirects = 0;
        for (const widgetDataItem of widgetData[year][month]) {
          totalClicks += widgetDataItem.clicks;
          totalRedirects += widgetDataItem.redirects;
        }
        clickSeries.push(totalClicks);
        redirectSeries.push(totalRedirects);
      }
    }

    const clicks = dictionary.chart_clicks;
    const redirects = dictionary.chart_redirects;

    return [
      { name: clicks, data: clickSeries },
      { name: redirects, data: redirectSeries },
    ];
  };

  if (!widgetData) {
    return (
      <div className="widgetChatWrapper">
        <Skeleton />
      </div>
    );
  }

  return (
    <div className="widgetChatWrapper">
      <Chart options={getOptions()} series={getSeries()} type="bar" width={'100%'} height={'100%'} />
    </div>
  );
}
